import { useEffect } from "react";
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from "react-router";
import * as Sentry from "@sentry/react";
import { DEV, getConfig } from "./lib/configMgr";

const {
  Sentry: sentryOptions,
  cell,
  env,
} = getConfig();

// When the browser fails one of the SDK's own uploads (a session update, on every
// navigation), the rejected fetch reaches the global unhandledrejection handlers twice:
// Sentry reports it as "TypeError: Failed to fetch" (WEBAPP-EJ), and index.js passes it
// to Analytics.exception, whose console.error captureConsoleIntegration reports again
// (WEBAPP-FX, WEBAPP-EK). The request is the SDK's, never ours: its stack runs through
// client.sendEnvelope. The gtag.js "replaceState" frame those issues are titled after is
// only where the navigation entered, since GA wraps history.replaceState. Our own failed
// fetches (WEBAPP-1T) have no sendEnvelope frame and still report.
const FETCH_FAILED_VALUE = /^(Failed to fetch|NetworkError when attempting to fetch resource\.|Load failed)$/;
const FETCH_FAILED_TEXT = /TypeError: (Failed to fetch|NetworkError when attempting to fetch resource|Load failed)/;
const SENTRY_UPLOAD_FRAME = /\bsendEnvelope\b/;

const isSentryUploadFailure = event => {
  const rejected = event?.exception?.values?.some(v => v.type === 'TypeError'
      && FETCH_FAILED_VALUE.test(v.value ?? '')
      && v.stacktrace?.frames?.some(f => SENTRY_UPLOAD_FRAME.test(f.function ?? '')));
  if (rejected) return true;
  // Console-captured copy: no TypeError to inspect, but the logged text carries its stack.
  const text = event?.message ?? event?.exception?.values?.[0]?.value ?? '';
  return FETCH_FAILED_TEXT.test(text) && SENTRY_UPLOAD_FRAME.test(text);
};

export const beforeSend = event => {
  if (event?.exception?.values) {
    for (const exception of event.exception.values) {
      if (exception.type === 'SearchCanceled') return null;
      if (exception.type === 'AbortError') return null;
      // NotAuthorizedException = wrong password, expired refresh token, or bot/scanner.
      // NotAuthorized = API 403 (expired session, missing authorization) — expected, not a bug.
      // Both are always expected user-facing conditions, never application bugs.
      // Suppress entirely — tracked in PostHog via Analytics.SIGNIN_FAILURE instead.
      if (exception.type === 'NotAuthorizedException') return null;
      if (exception.type === 'NotAuthorized') return null;
      // session-expired from LLMClient._handleError: captureMessage with asException:true
      // creates a synthetic Error whose name is the message text, not "NotAuthorized",
      // so the type-based check above misses it. Suppress via errorType tag instead.
      if (event?.tags?.errorType === 'session-expired') return null;
      // ServerOfflineError on non-prod environments (dev/staging) is expected —
      // beta/dev backends may be unavailable or timing out on analytics endpoints.
      if (exception.type === 'ServerOfflineError' && event.environment !== 'production') return null;
    }
  }

  if (event?.message && /^Gateway error \[\d+\], retry \d+\/\d+ after/.test(event.message)) {
    return null;
  }

  // request.js Sentry.captureMessage on 403 responses — same expired-session/unauthorized
  // condition as the thrown NotAuthorized above, but plain captureMessage has no
  // exception.values, so the type-based loop above never sees it. Tagged at the call site.
  if (event?.tags?.error_type === 'api_forbidden') return null;
  // SearchSessionStore's console.error when _runNewSearch rejects with NotAuthorized —
  // console-captured, so it has no exception.type either; match by message instead.
  if (event?.message?.includes('Unable to restore session information during search')) return null;
  // UserStore.refresh()'s console.error before throwing NotAuthorized — same expected
  // session-expired condition as the NotAuthorized suppression above, but console-captured
  // so it has no exception.type to match on (WEBAPP-BZ).
  if (event?.message?.includes('Token refresh failed - no token returned') ||
      event?.message?.includes('Token refresh returned same token')) return null;

  // PostHog self-rate-limits and logs via console.error; rrweb captures it
  // and Sentry treats it as an unhandled error. Not an application bug.
  const eventValue = event?.exception?.values?.[0]?.value ?? event?.message ?? '';
  if (eventValue.includes('[PostHog.js]')) return null;
  // The SDK's own upload failing (WEBAPP-EJ/PLEX-6479, WEBAPP-FX and WEBAPP-EK/PLEX-6612).
  if (isSentryUploadFailure(event)) return null;
  // Console-captured SearchCanceled: exception.type is null when rrweb/captureConsoleIntegration
  // wraps a console.error, so the typed check above misses it. Suppress by value string.
  if (eventValue.includes('SearchCanceled')) return null;
  // Console-captured form of ServerOfflineError: captureConsoleIntegration wraps
  // console.error calls as message events with no exception.type, so the typed
  // check above misses them. Apply the same staging-only suppression here.
  if (eventValue.includes('ServerOfflineError') && event.environment !== 'production') return null;
  // useWatchdog's diagnostic console.warn on a real fire duplicates the properly
  // fingerprinted/tagged captureMessage LLMClient._handleTimeout already sends —
  // suppress the raw console-captured copy (WEBAPP-A2).
  if (eventValue.includes('[useWatchdog] firing timer scheduled by')) return null;
  // Same duplication, one level up: _handleTimeout's own diagnostic console.warn
  // (logged just before it calls _handleError, which does the real capture)
  // also gets console-captured as a second, unfingerprinted issue (WEBAPP-9Z).
  if (eventValue.includes('[LLMClient] Watchdog firing:')) return null;
  // loadConversation's session-repair path (WEBAPP-5T/PLEX-4064, dup of PLEX-4076):
  // dropping an orphaned toolResult is the repair succeeding, not a regression of the
  // fixed bug. Already logged via console.debug (not captured by captureConsoleIntegration's
  // warn/error/assert levels), but stale cached bundles can still emit the old console.warn —
  // suppress by message so that noise doesn't reopen the closed ticket.
  if (eventValue.includes('loadConversation: dropping incomplete toolResult for')) return null;

  // _handleStreamError's diagnostic line (WEBAPP-EF, dup of WEBAPP-DJ): downgraded
  // console.warn -> console.debug in 2ccc44ab (2026-09-17) since it's per-call-unique
  // text that spawned a fresh noise issue per stream error. Already console.debug (not
  // captured by warn/error/assert levels), but stale cached bundles can still emit the
  // old console.warn — suppress by message so noise doesn't reopen the closed ticket.
  if (eventValue.includes('_handleStreamError closing root stream:')) return null;

  const eventText = event?.message ?? event?.exception?.values?.[0]?.value ?? '';
  if (/\[CognitoProvider\] getSession failed/.test(eventText)) return null;
  // NetworkError branch of the force-refresh handler (WEBAPP-7P/PLEX-5834) logs via
  // console.warn instead of captureMessage, but captureConsoleIntegration still
  // console-captures warn-level calls into Sentry — catch that copy too.
  if (/\[CognitoProvider\] force refresh getSession failed/.test(eventText)) return null;
  if (/\[UserStore\] Network error (during session restore|on retry)/.test(eventText)) return null;
  // UserStore.onSessionError's exhausted-retries tail of the same NetworkError
  // branch (WEBAPP-EH, dup of WEBAPP-7P/PLEX-5834): transient client-side network
  // blip, already surfaced to the user via toast.error and tracked in PostHog via
  // Analytics.SESSION_RESTORE -- not an app bug. Static message collapses unrelated
  // users/cells into one "recurring" issue; suppress like its sibling above.
  if (/\[UserStore\] Network error, exhausted retries, giving up/.test(eventText)) return null;
  if (event?.exception?.values?.some(v => v.type === 'Error' && v.value === 'Network error'
      && v.stacktrace?.frames?.some(f => f.filename?.includes('amazon-cognito-identity-js')))) {
    return null;
  }

  // Split generic JS error types by their actual message so Sentry
  // doesn't lump unrelated errors under "new Promise" or "Error".
  const primaryException = event?.exception?.values?.[0];
  if (primaryException) {
    // captureMessage() builds an exception interface (via eventFromString) whenever
    // attachStacktrace is on, but never sets `type` — Sentry's title algorithm falls
    // back to the literal "<unknown>" when type is empty, ignoring `value` entirely
    // (WEBAPP-7P). Backfill a generic type so the message text drives the title.
    if (!primaryException.type && primaryException.value) {
      primaryException.type = 'Message';
    }
    const type = primaryException.type || '';
    // ServerError is one class thrown from several call sites in request.js with
    // near-identical stacks, so Sentry's default type+stack fingerprint groups
    // AccessDenied/redis/ES/409/etc. failures into one bucket (WEBAPP-26) regardless
    // of which backend call actually failed. Split it by message like the other
    // generic types below.
    const GENERIC_TYPES = ['new Promise', 'Error', 'Object', 'null', 'undefined', 'ServerError', 'Message'];
    if (GENERIC_TYPES.includes(type) && primaryException.value) {
      const shortValue = primaryException.value.split('\n')[0].slice(0, 80);
      event.fingerprint = ['{{ default }}', shortValue];
    }
  }

  if (event?.logger === "console" || event?.breadcrumbs?.some(b => b.category === "console")) {
    const level = event.level || "log";
    if (event.exception && event?.message && /^(log|error|warn(ing)?|info|debug|assert)$/.test(level)) {
      const stack = event.exception.values[0].stacktrace;
      if (stack?.frames) {
        const offset = new Set(stack.frames.map(el => el.function)).has("captureMessage") ? 5 : 7;
        if (stack.frames.length >= offset) {
          stack.frames.length -= offset;
        }
      }
    }
  }
  return event;
};

if (sentryOptions?.DSN) {
  Sentry.init({
    dsn: sentryOptions.DSN,
    debug: false,
    enabled: true,
    sendDefaultPii: true,
    beforeSend,
    beforeSendTransaction: transaction => {
      const url = transaction.request?.url || transaction.transaction || '';
      if (url.includes('/api/history')) return null;
      return transaction;
    },
    integrations: [
      Sentry.captureConsoleIntegration({ levels: ["warn", "error", "assert"] }),
      Sentry.browserTracingIntegration({
        routingInstrumentation: Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      }),
      Sentry.replayIntegration(),
    ],
    release: sentryOptions.release,
    environment: cell === "beta" ? "staging" : env,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: DEV ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    maxValueLength: 8192,
    attachStacktrace: true,
  });

  Sentry.setTags(sentryOptions.tags);
}
